const React = require('react');
const Joi = require('joi');
const { default: Box } = require('@mui/material/Box');
const {
    RESOURCE_CREATION_METHODS,
    USER_ROLE_GROUPS_IDS,
    USER_ROLE_IDS
} = require('utils/constants');
const ResourceImageField = require('./ResourceImageField');

const FORM_ID = 'admin-resource-form';
const UPLOAD_FORM_ID = 'admin-resource-upload-form';
const DEFAULT_CTA_BG_COLOR = '#000000';
const DEFAULT_CTA_TEXT_COLOR = '#ffffff';
const ALL_ROLES_VALUE = '__ALL__';

module.exports.FORM_ID = FORM_ID;
module.exports.UPLOAD_FORM_ID = UPLOAD_FORM_ID;
module.exports.ALL_ROLES_VALUE = ALL_ROLES_VALUE;

module.exports.isImageUploadResource = function isImageUploadResource(resource) {

    return resource?.creationMethod === RESOURCE_CREATION_METHODS.IMAGE_UPLOAD;
};

module.exports.getResourceEditPath = function getResourceEditPath(resource) {

    if (!resource?.id) {
        return '/app/admin/resources';
    }

    if (module.exports.isImageUploadResource(resource)) {
        return `/app/admin/resources/${resource.id}/edit/upload`;
    }

    return `/app/admin/resources/${resource.id}/edit`;
};

module.exports.getProspectResourceRoles = function getProspectResourceRoles(roles = [], persistedRoleIds = []) {

    const persistedIds = new Set(persistedRoleIds.map(String));
    const matched = roles
        .filter((role) => {

            if (persistedIds.has(String(role.id)) || USER_ROLE_IDS.PROSPECT.includes(Number(role.id))) {
                return true;
            }

            if (role.roleGroupId != null) {
                return String(role.roleGroupId) === String(USER_ROLE_GROUPS_IDS.PROSPECT);
            }

            return false;
        })
        .map((role) => ({
            id: role.id,
            label: role.label || role.name
        }));
    const seen = new Set(matched.map((role) => String(role.id)));

    USER_ROLE_IDS.PROSPECT.forEach((id) => {

        if (seen.has(String(id))) {
            return;
        }

        const fromRoles = roles.find((role) => Number(role.id) === Number(id));
        matched.push({
            id,
            label: fromRoles?.label || fromRoles?.name || 'Prospect'
        });
        seen.add(String(id));
    });

    persistedRoleIds.forEach((id) => {

        if (seen.has(String(id))) {
            return;
        }

        const fromRoles = roles.find((role) => String(role.id) === String(id));
        matched.push({
            id: Number(id),
            label: fromRoles?.label || fromRoles?.name || `Role ${id}`
        });
        seen.add(String(id));
    });

    return matched;
};

module.exports.resolveProspectRoleSelection = function resolveProspectRoleSelection({
    allRoleIds = [],
    previousAllProspectsSelected = false,
    value = []
} = {}) {

    const roleIds = value.filter((roleId) => roleId !== ALL_ROLES_VALUE);
    const allProspectsSelected = value.includes(ALL_ROLES_VALUE);

    if (allProspectsSelected && !previousAllProspectsSelected) {
        return {
            roleIds: allRoleIds,
            allProspectsSelected: true
        };
    }

    if (allProspectsSelected) {
        return {
            roleIds,
            allProspectsSelected: false
        };
    }

    return {
        roleIds: previousAllProspectsSelected ? [] : roleIds,
        allProspectsSelected: false
    };
};

module.exports.EMPTY_DEFAULTS = {
    brandName: '',
    brandLogoUrl: '',
    tagText: '',
    tagColor: '#000000',
    tagTextColor: '#ffffff',
    tagFont: '',
    title: '',
    subtitle: '',
    mediaType: '',
    heroImageUrl: '',
    heroVideoUrl: '',
    couponCode: '',
    terms: '',
    expirationDate: null,
    ctaText: 'Learn more',
    ctaUrl: '',
    ctaDeepLink: '',
    ctaBgColor: '',
    ctaTextColor: '',
    startDate: null,
    endDate: null,
    status: 'draft',
    priority: 100,
    order: '',
    sendPushNotifications: false
};

module.exports.UPLOAD_EMPTY_DEFAULTS = {
    brandName: '',
    brandLogoUrl: '',
    tagText: '',
    tagColor: '#000000',
    tagTextColor: '',
    tagFont: '',
    title: '',
    ctaText: 'Learn more',
    ctaUrl: '',
    ctaDeepLink: '',
    ctaBgColor: '',
    ctaTextColor: '',
    status: 'draft',
    priority: 100,
    startDate: null,
    endDate: null,
    order: '',
    sendPushNotifications: false
};

module.exports.withCtaColorDefaults = function withCtaColorDefaults(defaults = {}) {

    return {
        ...defaults,
        ctaBgColor: defaults.ctaBgColor || DEFAULT_CTA_BG_COLOR,
        ctaTextColor: defaults.ctaTextColor || DEFAULT_CTA_TEXT_COLOR
    };
};

module.exports.shouldConfirmPushNotifications = function shouldConfirmPushNotifications(defaults = {}, formData = {}) {

    return !defaults.sendPushNotifications && !!formData.sendPushNotifications;
};

module.exports.buildFormConfig = function buildFormConfig(props = {}) {

    const { defaults, onSubmit } = props;

    return {
        formId: FORM_ID,
        fields: internals.fields,
        defaults: defaults || module.exports.EMPTY_DEFAULTS,
        schema: internals.schema,
        onSubmit
    };
};

module.exports.buildUploadFormConfig = function buildUploadFormConfig(props = {}) {

    const { defaults, onSubmit } = props;

    return {
        formId: UPLOAD_FORM_ID,
        fields: internals.uploadFields,
        defaults: defaults || module.exports.UPLOAD_EMPTY_DEFAULTS,
        schema: internals.uploadSchema,
        onSubmit
    };
};

module.exports.buildTargetingRowFormConfig = function buildTargetingRowFormConfig(props = {}) {

    const {
        allProspectsSelected = false,
        availableRoles = [],
        isLoading = false,
        onRolesChange,
        onSchoolChange,
        roleIds = [],
        rowKey,
        schoolId,
        schools = [],
        schoolsLoading = false
    } = props;
    const prospectRoleIds = allProspectsSelected
        ? [ALL_ROLES_VALUE, ...roleIds]
        : roleIds;

    return {
        formId: `resource-targeting-row-${rowKey}`,
        defaults: {
            schoolId: schoolId || '',
            prospectRoleIds
        },
        fields: [{
            type: 'group',
            columns: 2,
            gap: 1,
            fields: [
                {
                    name: 'schoolId',
                    type: 'SelectField',
                    label: 'School',
                    options: schools.map((school) => ({
                        value: school.id,
                        label: school.name
                    })),
                    onChange: onSchoolChange,
                    textFieldProps: {
                        id: `target-school-${rowKey}`,
                        variant: 'filled',
                        disabled: schoolsLoading,
                        InputLabelProps: {
                            shrink: true
                        }
                    }
                },
                {
                    name: 'prospectRoleIds',
                    type: 'MultiSelectField',
                    label: 'Prospect roles',
                    options: [
                        { value: ALL_ROLES_VALUE, label: 'All Prospects' },
                        ...availableRoles.map((role) => ({
                            value: role.id,
                            label: role.label
                        }))
                    ],
                    onChange: onRolesChange,
                    renderValue: () => isLoading
                        ? 'Loading…'
                        : internals.renderTargetRoleValue(
                            availableRoles,
                            roleIds,
                            allProspectsSelected
                        ),
                    formControlProps: {
                        disabled: !schoolId || isLoading || availableRoles.length === 0,
                        variant: 'filled'
                    },
                    inputLabelProps: {
                        shrink: true
                    },
                    selectProps: {
                        displayEmpty: true,
                        labelId: `target-prospect-roles-${rowKey}-label`,
                        inputProps: {
                            id: `target-prospect-roles-${rowKey}`
                        }
                    }
                }
            ]
        }],
        onSubmit: internals.noop
    };
};

module.exports.buildImageResourcePayload = function buildImageResourcePayload(imageUrl, formData) {

    return {
        brandName: formData.brandName,
        brandLogoUrl: formData.brandLogoUrl,
        tagText: formData.tagText || null,
        tagColor: formData.tagColor || null,
        tagTextColor: formData.tagTextColor || null,
        ctaBgColor: formData.ctaBgColor || null,
        ctaTextColor: formData.ctaTextColor || null,
        tagFont: formData.tagFont || null,
        title: formData.title,
        mediaType: 'image',
        heroImageUrl: imageUrl,
        ctaText: formData.ctaText,
        ctaUrl: formData.ctaUrl,
        ctaDeepLink: formData.ctaDeepLink || null,
        status: formData.status,
        priority: internals.clampPriority(formData.priority),
        startDate: formData.startDate,
        endDate: formData.endDate,
        order: formData.order !== '' && formData.order != null ? Number(formData.order) : null,
        sendPushNotifications: !!formData.sendPushNotifications
    };
};

module.exports.buildResourcePayload = function buildResourcePayload(formData = {}) {

    return Object.entries(formData).reduce((payload, [key, value]) => {

        if (typeof value === 'undefined') {
            return payload;
        }

        if (typeof value?.toISOString === 'function') {
            payload[key] = value.toISOString();
            return payload;
        }

        if (internals.NUMBER_FIELDS.has(key)) {
            payload[key] = internals.normalizeNumberField(key, value);
            return payload;
        }

        payload[key] = value === '' ? null : value;
        return payload;
    }, {});
};

const internals = {};

internals.noop = () => undefined;

internals.renderTargetRoleValue = (availableRoles, roleIds, allProspectsSelected) => {

    if (roleIds.length === 0) {
        return <Box component='span' sx={{ color: 'text.secondary' }}>Select prospect roles</Box>;
    }

    if (allProspectsSelected) {
        return 'All Prospects';
    }

    return availableRoles
        .filter((role) => roleIds.includes(role.id))
        .map((role) => role.label)
        .join(', ');
};

internals.NUMBER_FIELDS = new Set(['order', 'priority']);

internals.PRIORITY_MIN = 1;
internals.PRIORITY_MAX = 100;
internals.PRIORITY_DEFAULT = 100;

internals.clampPriority = (value) => {

    if (value === '' || value === null || typeof value === 'undefined') {
        return internals.PRIORITY_DEFAULT;
    }

    const numberValue = Math.trunc(Number(value));

    if (Number.isNaN(numberValue)) {
        return internals.PRIORITY_DEFAULT;
    }

    return Math.min(internals.PRIORITY_MAX, Math.max(internals.PRIORITY_MIN, numberValue));
};

internals.priorityFieldProps = {
    inputProps: {
        inputMode: 'numeric'
    }
};

internals.prioritySchema = Joi.number()
    .integer()
    .min(internals.PRIORITY_MIN)
    .max(internals.PRIORITY_MAX)
    .allow('', null)
    .messages({
        'number.base': 'Priority must be a whole number — only digits 0-9 are allowed',
        'number.integer': 'Priority must be a whole number — decimals are not allowed',
        'number.unsafe': 'Priority must be a whole number — only digits 0-9 are allowed',
        'number.min': `Priority must be between ${internals.PRIORITY_MIN} and ${internals.PRIORITY_MAX}`,
        'number.max': `Priority must be between ${internals.PRIORITY_MIN} and ${internals.PRIORITY_MAX}`
    });

internals.normalizeNumberField = (field, value) => {

    if (field === 'priority') {
        return internals.clampPriority(value);
    }

    if (value === '' || value === null) {
        return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
};

internals.STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'disabled', label: 'Disabled' }
];

internals.MEDIA_TYPE_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' }
];

internals.brandSectionFields = [
    { type: 'title', text: 'Brand' },
    { name: 'brandName', label: 'Brand Name', type: 'TextField', required: true, fullWidth: true },
    {
        name: 'brandLogoUrl',
        label: 'Brand Logo',
        type: 'TextField',
        required: true,
        fullWidth: true,
        component: ResourceImageField,
        componentProps: {
            required: true
        }
    },
    { name: 'tagText', label: 'Tag Text (e.g. "New", "Exclusive")', type: 'TextField', fullWidth: true },
    { name: 'tagColor', label: 'Tag Color (hex, e.g. #e53935)', type: 'ColorField', fullWidth: false },
    { name: 'tagTextColor', label: 'Tag Text Color (RGB)', type: 'ColorField', fullWidth: false },
    { name: 'tagFont', label: 'Tag Font (optional)', type: 'TextField', fullWidth: true }
];

internals.uploadFields = [
    ...internals.brandSectionFields,
    { type: 'divider' },
    { type: 'title', text: 'Content' },
    { name: 'title', label: 'Title', type: 'TextField', required: true, fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'CTA' },
    { name: 'ctaText', label: 'CTA Button Label (e.g. "Learn more")', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaBgColor', label: 'CTA Background Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaTextColor', label: 'CTA Text Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaUrl', label: 'CTA URL', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaDeepLink', label: 'CTA Deep Link (optional)', type: 'TextField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'Scheduling & Status' },
    { name: 'status', label: 'Status', type: 'SelectField', fullWidth: true, options: internals.STATUS_OPTIONS },
    { name: 'sendPushNotifications', label: 'Send this resource as a push notification', type: 'SwitchField', offLabel: 'In-app only', onLabel: 'Send push', fullWidth: true },
    { name: 'priority', label: 'Priority (1-100, lower = higher priority)', type: 'TextField', fullWidth: true, textFieldProps: internals.priorityFieldProps },
    { name: 'startDate', label: 'Start Date', type: 'DateTimeField', fullWidth: true },
    { name: 'endDate', label: 'End Date', type: 'DateTimeField', fullWidth: true },
    { name: 'order', label: 'Carousel Order (optional)', type: 'TextField', fullWidth: true, textFieldProps: { type: 'number' } }
];

internals.uploadSchema = Joi.object({
    brandName: Joi.string().required().messages({ 'string.empty': 'Brand Name is required' }),
    brandLogoUrl: Joi.string().required().messages({ 'string.empty': 'Brand Logo is required' }),
    title: Joi.string().required().messages({ 'string.empty': 'Title is required' }),
    tagText: Joi.string().allow('', null),
    tagColor: Joi.string().allow('', null),
    tagTextColor: Joi.string().allow('', null),
    tagFont: Joi.string().allow('', null),
    ctaText: Joi.string().required().messages({ 'string.empty': 'CTA Button Label is required' }),
    ctaUrl: Joi.string().required().messages({ 'string.empty': 'CTA URL is required' }),
    ctaBgColor: Joi.string().allow('', null),
    ctaTextColor: Joi.string().allow('', null),
    ctaDeepLink: Joi.string().allow('', null),
    status: Joi.string().valid('draft', 'pending', 'approved', 'rejected', 'disabled'),
    sendPushNotifications: Joi.boolean(),
    priority: internals.prioritySchema,
    startDate: Joi.any().allow(null),
    endDate: Joi.any().allow(null),
    order: Joi.any().allow('', null)
});

internals.fields = [
    ...internals.brandSectionFields,
    { type: 'divider' },
    { type: 'title', text: 'Content' },
    { name: 'title', label: 'Title', type: 'TextField', required: true, fullWidth: true },
    { name: 'subtitle', label: 'Body Text', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 2 } },
    { name: 'mediaType', label: 'Media Type', type: 'SelectField', fullWidth: true, options: internals.MEDIA_TYPE_OPTIONS },
    { name: 'heroImageUrl', label: 'Hero Image URL', type: 'TextField', fullWidth: true },
    { name: 'heroVideoUrl', label: 'Hero Video URL', type: 'TextField', fullWidth: true },
    { name: 'couponCode', label: 'Coupon Code', type: 'TextField', fullWidth: true },
    { name: 'terms', label: 'Terms & Conditions', type: 'TextareaField', fullWidth: true, textFieldProps: { minRows: 3 } },
    { name: 'expirationDate', label: 'Expiration Date', type: 'DateTimeField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'CTA' },
    { name: 'ctaText', label: 'CTA Button Label (e.g. "Learn more")', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaBgColor', label: 'CTA Background Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaTextColor', label: 'CTA Text Color', type: 'ColorField', fullWidth: false },
    { name: 'ctaUrl', label: 'CTA URL', type: 'TextField', required: true, fullWidth: true },
    { name: 'ctaDeepLink', label: 'CTA Deep Link (optional)', type: 'TextField', fullWidth: true },
    { type: 'divider' },
    { type: 'title', text: 'Scheduling & Status' },
    { name: 'status', label: 'Status', type: 'SelectField', fullWidth: true, options: internals.STATUS_OPTIONS },
    { name: 'sendPushNotifications', label: 'Send this resource as a push notification', type: 'SwitchField', offLabel: 'In-app only', onLabel: 'Send push', fullWidth: true },
    { name: 'priority', label: 'Priority (1-100, lower = higher priority)', type: 'TextField', fullWidth: true, textFieldProps: internals.priorityFieldProps },
    { name: 'startDate', label: 'Start Date', type: 'DateTimeField', fullWidth: true },
    { name: 'endDate', label: 'End Date', type: 'DateTimeField', fullWidth: true },
    { name: 'order', label: 'Carousel Order (optional)', type: 'TextField', fullWidth: true, textFieldProps: { type: 'number' } }
];

internals.schema = Joi.object({
    brandName: Joi.string().required().messages({ 'string.empty': 'Brand Name is required' }),
    brandLogoUrl: Joi.string().required().messages({ 'string.empty': 'Brand Logo is required' }),
    title: Joi.string().required().messages({ 'string.empty': 'Title is required' }),
    ctaText: Joi.string().required().messages({ 'string.empty': 'CTA Button Label is required' }),
    ctaUrl: Joi.string().required().messages({ 'string.empty': 'CTA URL is required' }),
    tagText: Joi.string().allow('', null),
    tagColor: Joi.string().allow('', null),
    tagTextColor: Joi.string().allow('', null),
    tagFont: Joi.string().allow('', null),
    subtitle: Joi.string().allow('', null),
    mediaType: Joi.string().valid('image', 'video').allow('', null),
    heroImageUrl: Joi.string().allow('', null),
    heroVideoUrl: Joi.string().allow('', null),
    couponCode: Joi.string().allow('', null),
    terms: Joi.string().allow('', null),
    expirationDate: Joi.any().allow(null),
    ctaDeepLink: Joi.string().allow('', null),
    ctaBgColor: Joi.string().allow('', null),
    ctaTextColor: Joi.string().allow('', null),
    startDate: Joi.any().allow(null),
    endDate: Joi.any().allow(null),
    status: Joi.string().valid('draft', 'pending', 'approved', 'rejected', 'disabled'),
    sendPushNotifications: Joi.boolean(),
    priority: internals.prioritySchema,
    order: Joi.any().allow('', null)
}).options({ allowUnknown: true });
